import { useState, useMemo } from 'react';
import { Ability, CharacterWithSkills, SkillProficiencies, ClassName, SkillName, SkillBonusOverrides, ProficiencyLevel } from '@shared/types';
import { CLASSES, SKILLS, ABILITY_NAMES, LANGUAGES, TOOL_PROFICIENCIES } from '@shared/dnd-data';
import { abilityModifier, savingThrow, skillBonus, calculateSkillBonuses } from '@shared/calculations';
import ProficiencyCircle from './ProficiencyCircle';
import TagInputWithSuggestions from './TagInputWithSuggestions';

// WHY: Case-insensitive, order-independent comparison of two string arrays so the
// dirty check treats ["Common","Elvish"] and ["Elvish","Common"] as equal (reordering
// free-text tags shouldn't flag an unsaved change).
function sameStringArray(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const norm = (arr: string[]) => [...arr].map((s) => s.toLowerCase()).sort();
  const na = norm(a);
  const nb = norm(b);
  return na.every((v, i) => v === nb[i]);
}

// WHY: Normalize a raw override string for display. The "+" is display-only sugar — the
// underlying numeric override is always a plain integer. We keep a lone "-" (no digits yet)
// so the user can keep typing a negative number, but strip every stray character down to the
// sign + digits. Positives get a leading "+" to match D&D modifier notation.
function formatOverrideInput(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  const negative = trimmed.startsWith('-');
  const digits = trimmed.replace(/[^\d]/g, '');
  if (digits === '') return negative ? '-' : '';
  return (negative ? '-' : '+') + digits;
}

// WHY: Pure helper comparing live form state against the originally loaded
// character. Returning both `dirty` and a per-field `fieldDirty` map lets the UI
// (a) hide/show the save affordance and (b) highlight only the changed inputs.
// Kept pure (no hooks) so it is unit-testable without simulating React events.
export interface FormState {
  name: string;
  className: ClassName;
  abilityScores: Record<string, number | ''>;
  skills: SkillProficiencies;
  savingThrowOverrides: Record<string, number | null>;
  skillBonusOverrides: SkillBonusOverrides;
  languages: string[];
  toolProficiencies: string[];
}

export function computeDirtyState(
  initialData: CharacterWithSkills,
  state: FormState
): { dirty: boolean; fieldDirty: Record<string, boolean> } {
  const fieldDirty: Record<string, boolean> = {};

  // WHY: An empty ability score field coerces to 10 for math/submit, so a blank
  // field must compare equal to a stored 10 — otherwise a fresh edit would look dirty.
  const scoreValues = Object.fromEntries(
    (Object.keys(ABILITY_NAMES) as Ability[]).map((a) => [
      a,
      state.abilityScores[a] === '' ? 10 : (state.abilityScores[a] as number),
    ])
  ) as Record<Ability, number>;

  const initialScores = {
    strength: initialData.strength,
    dexterity: initialData.dexterity,
    constitution: initialData.constitution,
    intelligence: initialData.intelligence,
    wisdom: initialData.wisdom,
    charisma: initialData.charisma,
  } as Record<Ability, number>;

  if (state.name !== initialData.name) fieldDirty.name = true;
  if (state.className !== initialData.class) fieldDirty.class = true;
  (Object.keys(scoreValues) as Ability[]).forEach((a) => {
    if (scoreValues[a] !== initialScores[a]) fieldDirty[`score:${a}`] = true;
  });

  Object.keys(state.skills).forEach((s) => {
    if (state.skills[s as SkillName] !== initialData.skills[s as SkillName]) fieldDirty[`skill:${s}`] = true;
  });

  // WHY: Overrides are stored as plain integers; the "+"/"-" in the input is
  // display-only sugar, so compare the numeric values, not the formatted strings.
  const overridePairs: [string, number | null, number | null][] = [
    ['strength', state.savingThrowOverrides.strength, initialData.saving_throw_override_str],
    ['dexterity', state.savingThrowOverrides.dexterity, initialData.saving_throw_override_dex],
    ['constitution', state.savingThrowOverrides.constitution, initialData.saving_throw_override_con],
    ['intelligence', state.savingThrowOverrides.intelligence, initialData.saving_throw_override_int],
    ['wisdom', state.savingThrowOverrides.wisdom, initialData.saving_throw_override_wis],
    ['charisma', state.savingThrowOverrides.charisma, initialData.saving_throw_override_cha],
  ];
  overridePairs.forEach(([key, cur, orig]) => {
    if (cur !== orig) fieldDirty[`save:${key}`] = true;
  });

  const initialSkillOverrides = initialData.skillBonusOverrides || ({} as SkillBonusOverrides);
  Object.keys(state.skillBonusOverrides).forEach((s) => {
    if (state.skillBonusOverrides[s as SkillName] !== initialSkillOverrides[s as SkillName])
      fieldDirty[`skillOverride:${s}`] = true;
  });

  // WHY: Languages/tools are order-independent free-text lists — compare with the
  // case-insensitive helper, not reference/order equality.
  if (!sameStringArray(state.languages, initialData.languages || [])) fieldDirty.languages = true;
  if (!sameStringArray(state.toolProficiencies, initialData.toolProficiencies || []))
    fieldDirty.toolProficiencies = true;

  return { dirty: Object.keys(fieldDirty).length > 0, fieldDirty };
}

interface Props {
  initialData: CharacterWithSkills | null;
  onSubmit: (data: {
    name: string;
    class: ClassName;
    abilityScores: Record<string, number>;
    skills: SkillProficiencies;
    savingThrowOverrides: Record<string, number | null>;
    skillBonusOverrides: SkillBonusOverrides;
    languages: string[];
    toolProficiencies: string[];
  }) => Promise<void>;
  saving: boolean;
  campaignLevel: number;
}

export default function CharacterForm({ initialData, onSubmit, saving, campaignLevel }: Props) {
  const [name, setName] = useState(initialData?.name || '');
  const [className, setClassName] = useState<ClassName>(initialData?.class || 'fighter');
  // WHY: For a new character the ability score fields start EMPTY so mobile
  // users can type a value directly instead of deleting a prefilled "10".
  // An empty field is coerced to the D&D baseline of 10 on submit and for math
  // (see coercedAbilityScores / handleSubmit), so leaving it blank == 10.
  const [abilityScores, setAbilityScores] = useState<Record<string, number | ''>>(() => {
    if (initialData) {
      return {
        strength: initialData.strength,
        dexterity: initialData.dexterity,
        constitution: initialData.constitution,
        intelligence: initialData.intelligence,
        wisdom: initialData.wisdom,
        charisma: initialData.charisma,
      };
    }
    return {
      strength: '',
      dexterity: '',
      constitution: '',
      intelligence: '',
      wisdom: '',
      charisma: '',
    };
  });
  const [skills, setSkills] = useState<SkillProficiencies>(
    initialData?.skills || {
      acrobatics: 'none',
      animal_handling: 'none',
      arcana: 'none',
      athletics: 'none',
      deception: 'none',
      history: 'none',
      insight: 'none',
      intimidation: 'none',
      investigation: 'none',
      medicine: 'none',
      nature: 'none',
      perception: 'none',
      performance: 'none',
      persuasion: 'none',
      religion: 'none',
      sleight_of_hand: 'none',
      stealth: 'none',
      survival: 'none',
    }
  );
  const [savingThrowOverrides, setSavingThrowOverrides] = useState<Record<string, number | null>>({
    strength: initialData?.saving_throw_override_str ?? null,
    dexterity: initialData?.saving_throw_override_dex ?? null,
    constitution: initialData?.saving_throw_override_con ?? null,
    intelligence: initialData?.saving_throw_override_int ?? null,
    wisdom: initialData?.saving_throw_override_wis ?? null,
    charisma: initialData?.saving_throw_override_cha ?? null,
  });
  // WHY: Parallel raw string state for saving throw overrides. React controlled inputs
  // reset to the state value on every keystroke, so typing a leading minus sign
  // (e.g. "-3") would be cleared by parseInt("-") === NaN. The raw state preserves
  // intermediate input like "-" until the user blurs the field.
  const [savingThrowRaw, setSavingThrowRaw] = useState<Record<string, string>>(() => {
    if (!initialData) {
      return { strength: '', dexterity: '', constitution: '', intelligence: '', wisdom: '', charisma: '' };
    }
    return {
      strength: formatOverrideInput(String(initialData.saving_throw_override_str ?? '')),
      dexterity: formatOverrideInput(String(initialData.saving_throw_override_dex ?? '')),
      constitution: formatOverrideInput(String(initialData.saving_throw_override_con ?? '')),
      intelligence: formatOverrideInput(String(initialData.saving_throw_override_int ?? '')),
      wisdom: formatOverrideInput(String(initialData.saving_throw_override_wis ?? '')),
      charisma: formatOverrideInput(String(initialData.saving_throw_override_cha ?? '')),
    };
  });
  // WHY: New characters seed ["Common"] (PHB baseline every character knows).
  // Editing an existing character uses its stored lists; tool proficiencies have no
  // default and may legitimately be empty.
  const [languages, setLanguages] = useState<string[]>(
    initialData ? initialData.languages || [] : ['Common']
  );
  const [toolProficiencies, setToolProficiencies] = useState<string[]>(
    initialData ? initialData.toolProficiencies || [] : []
  );

  const [skillBonusOverrides, setSkillBonusOverrides] = useState<SkillBonusOverrides>(() => {
    const initial = initialData?.skillBonusOverrides;
    if (initial && Object.keys(initial).length > 0) return initial;
    const empty: Record<SkillName, number | null> = {
      acrobatics: null,
      animal_handling: null,
      arcana: null,
      athletics: null,
      deception: null,
      history: null,
      insight: null,
      intimidation: null,
      investigation: null,
      medicine: null,
      nature: null,
      perception: null,
      performance: null,
      persuasion: null,
      religion: null,
      sleight_of_hand: null,
      stealth: null,
      survival: null,
    };
    return empty;
  });
  // WHY: Parallel raw string state for skill bonus overrides. Same controlled-input
  // issue as saving throws: typing "-" must not be cleared by parseInt.
  const [skillBonusRaw, setSkillBonusRaw] = useState<Record<SkillName, string>>(() => {
    if (!initialData || !initialData.skillBonusOverrides) {
      const empty = {} as Record<SkillName, string>;
      for (const skill of Object.values(SKILLS)) {
        empty[skill.name] = '';
      }
      return empty;
    }
    const result = {} as Record<SkillName, string>;
    for (const skill of Object.values(SKILLS)) {
      // WHY: Use explicit null/undefined check instead of ?? to avoid transpilation
      // edge cases where String(undefined ?? '') could render as "undefined" in
      // static markup.
      const raw = initialData.skillBonusOverrides[skill.name];
      result[skill.name] = raw == null ? '' : formatOverrideInput(String(raw));
    }
    return result;
  });

  // WHY: Coerce an empty ability score ('') to the D&D baseline of 10 for all
  // calculations and for the onSubmit payload, so an unset field behaves like 10.
  const coercedAbilityScores = Object.fromEntries(
    (Object.keys(ABILITY_NAMES) as Ability[]).map((a) => [
      a,
      typeof abilityScores[a] === 'number' ? (abilityScores[a] as number) : 10,
    ])
  ) as Record<Ability, number>;

  const calculatedSkillBonuses = calculateSkillBonuses(coercedAbilityScores, skills, campaignLevel, skillBonusOverrides);

  const isEdit = initialData !== null;

  // WHY: Derive "dirty" by comparing the live form state against the originally
  // loaded character. This drives the unsaved-changes affordance (hide inline
  // Update button, show floating button, and highlight changed fields). Create
  // mode (initialData === null) is never considered dirty so the Create button
  // and field highlights stay inactive.
  const { dirty, fieldDirty } = useMemo(() => {
    if (!initialData) return { dirty: false, fieldDirty: {} as Record<string, boolean> };
    return computeDirtyState(initialData, {
      name,
      className,
      abilityScores,
      skills,
      savingThrowOverrides,
      skillBonusOverrides,
      languages,
      toolProficiencies,
    });
  }, [
    initialData,
    name,
    className,
    abilityScores,
    skills,
    savingThrowOverrides,
    skillBonusOverrides,
    languages,
    toolProficiencies,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const submittedAbilityScores = Object.fromEntries(
      (Object.keys(ABILITY_NAMES) as Ability[]).map((a) => [
        a,
        typeof abilityScores[a] === 'number' ? (abilityScores[a] as number) : 10,
      ])
    ) as Record<Ability, number>;
    await onSubmit({
      name,
      class: className,
      abilityScores: submittedAbilityScores,
      skills,
      savingThrowOverrides,
      skillBonusOverrides,
      languages,
      toolProficiencies,
    });
  };

  const updateAbilityScore = (ability: string, raw: string) => {
    if (raw === '') {
      setAbilityScores((prev) => ({ ...prev, [ability]: '' }));
      return;
    }
    const num = parseInt(raw, 10);
    if (Number.isNaN(num)) return;
    setAbilityScores((prev) => ({ ...prev, [ability]: Math.max(1, Math.min(30, num)) }));
  };

  const updateSkill = (skill: SkillName, value: 'none' | 'proficient' | 'expertise') => {
    setSkills((prev) => ({ ...prev, [skill]: value }));
  };

  const updateOverride = (ability: string, value: string) => {
    const formatted = formatOverrideInput(value);
    setSavingThrowRaw((prev) => ({ ...prev, [ability]: formatted }));
    if (formatted === '') {
      setSavingThrowOverrides((prev) => ({ ...prev, [ability]: null }));
    } else if (/^[+-]?\d+$/.test(formatted)) {
      const num = parseInt(formatted, 10);
      setSavingThrowOverrides((prev) => ({ ...prev, [ability]: num }));
    }
  };

  const finalizeOverride = (ability: string) => {
    const raw = savingThrowRaw[ability];
    const num = raw === '' ? null : parseInt(raw, 10);
    setSavingThrowOverrides((prev) => ({ ...prev, [ability]: Number.isNaN(num) ? null : num }));
    setSavingThrowRaw((prev) => ({ ...prev, [ability]: num === null ? '' : formatOverrideInput(String(num)) }));
  };

  const updateSkillBonusOverride = (skill: SkillName, value: string) => {
    const formatted = formatOverrideInput(value);
    setSkillBonusRaw((prev) => ({ ...prev, [skill]: formatted }));
    if (formatted === '') {
      setSkillBonusOverrides((prev) => ({ ...prev, [skill]: null }));
    } else if (/^[+-]?\d+$/.test(formatted)) {
      const num = parseInt(formatted, 10);
      setSkillBonusOverrides((prev) => ({ ...prev, [skill]: num }));
    }
  };

  const finalizeSkillBonusOverride = (skill: SkillName) => {
    const raw = skillBonusRaw[skill];
    const num = raw === '' ? null : parseInt(raw, 10);
    setSkillBonusOverrides((prev) => ({ ...prev, [skill]: Number.isNaN(num) ? null : num }));
    setSkillBonusRaw((prev) => ({ ...prev, [skill]: num === null ? '' : formatOverrideInput(String(num)) }));
  };

  return (
    // WHY: Extra bottom padding only when the floating save bar is shown, so the
    // fixed bar never covers the last card/input on small screens.
    <form onSubmit={handleSubmit} className={`space-y-6${isEdit && dirty ? ' pb-24' : ''}`}>
      <div className="card">
        <h3 className="heading text-lg font-semibold mb-4">Basic Info</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`input${fieldDirty.name ? ' dirty-input' : ''}`}
              required
            />
          </div>
          <div>
            <label className="label">Starting Class</label>
            <select
              value={className}
              onChange={(e) => setClassName(e.target.value as ClassName)}
              className={`select${fieldDirty.class ? ' dirty-input' : ''}`}
            >
              {Object.values(CLASSES).map((cls) => (
                <option key={cls.name} value={cls.name}>
                  {cls.displayName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="heading text-lg font-semibold mb-4">Ability Scores</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(ABILITY_NAMES).map(([key, displayName]) => {
            const score =
              typeof abilityScores[key] === 'number' ? (abilityScores[key] as number) : 10;
            const mod = abilityModifier(score);

            return (
            <div key={key}>
              <label className="label">{displayName} {mod >= 0 ? '+' : ''}{mod}</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={abilityScores[key] === '' ? '' : String(abilityScores[key])}
                onChange={(e) => updateAbilityScore(key, e.target.value)}
                className={`input${fieldDirty[`score:${key}`] ? ' dirty-input' : ''}`}
              />
            </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <h3 className="heading text-lg font-semibold mb-4">Saving Throws</h3>
        <p className="text-sm text-dnd-help mb-4">
          Modifier calculated from ability scores and starting class. Override if needed.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(ABILITY_NAMES).map(([key, displayName]) => {
            const autoValue = savingThrow(
              coercedAbilityScores[key as Ability],
              className,
              key as Ability,
              campaignLevel,
              null
            );
            const override = savingThrowOverrides[key];

            return (
                <div key={key}>
                  <label className="label">{displayName} Save</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    // WHY: Pattern must accept an optional leading "+" because
                    // formatOverrideInput renders positives with a "+" (display-only
                    // D&D modifier notation). The old "-?[0-9]*" rejected "+3" and
                    // triggered the browser "Please match the requested format" error,
                    // blocking submit even though the stored value is a plain integer.
                    pattern="[+-]?[0-9]*"
                    value={savingThrowRaw[key]}
                    placeholder={`${autoValue >= 0 ? '+' : ''}${autoValue}`}
                    onChange={(e) => updateOverride(key, e.target.value)}
                    onBlur={() => finalizeOverride(key)}
                    className={`input flex-1${fieldDirty[`save:${key}`] ? ' dirty-input' : ''}`}
                  />
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        {/* WHY: Skills and Skill Bonuses were merged into one card because the two panels
            duplicated every skill name. The proficiency circle and the bonus override now
            live side-by-side per skill, keeping the sheet compact and avoiding redundancy. */}
        <h3 className="heading text-lg font-semibold mb-4">Skills</h3>
        <p className="text-sm text-dnd-help mb-4">
          Tap the circle to set proficiency (none → proficient → expertise). Bonuses
          auto-calculate from ability scores, proficiency, and level. Override if needed.
        </p>
        <div className="space-y-3">
          {Object.values(SKILLS).map((skill) => {
            const autoValue = calculatedSkillBonuses[skill.name];
            const override = skillBonusOverrides[skill.name];

            return (
              <div key={skill.name} className="flex items-center gap-3 p-2 panel">
                {/* WHY: Fixed-width label so it cannot stretch and shove the input
                    around; truncate keeps long names (e.g. "Sleight of Hand") tidy. */}
                <span className="text-sm w-32 shrink-0 truncate">{skill.displayName}</span>
                    {/* WHY: flex-1 + justify-end parks the override input on the RIGHT
                        of the row. */}
                     <div className="flex-1 flex items-center justify-end">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[+-]?[0-9]*"
                        aria-label={`${skill.displayName} bonus override`}
                        value={skillBonusRaw[skill.name]}
                        placeholder={`${autoValue >= 0 ? '+' : ''}${autoValue}`}
                        onChange={(e) => updateSkillBonusOverride(skill.name, e.target.value)}
                        onBlur={() => finalizeSkillBonusOverride(skill.name)}
                        className={`w-32 px-3 py-2 min-h-[44px] bg-dnd-dark border border-dnd-border rounded-md text-dnd-input-text text-left placeholder-dnd-placeholder focus:outline-none focus:border-dnd-accent focus:ring-1 focus:ring-dnd-accent${fieldDirty[`skillOverride:${skill.name}`] ? ' dirty-input' : ''}`}
                      />
                    </div>
                <div className="shrink-0">
                  <ProficiencyCircle
                    value={skills[skill.name]}
                    onChange={(v: ProficiencyLevel) => updateSkill(skill.name, v)}
                    highlight={fieldDirty[`skill:${skill.name}`]}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <h3 className="heading text-lg font-semibold mb-4">Languages</h3>
        <p className="text-sm text-dnd-help mb-4">
          Type to search Player's Handbook languages, or add your own. Every
          character starts with Common, remove it if your character doesn't know any.
        </p>
        <TagInputWithSuggestions
          label="Add a language"
          suggestions={LANGUAGES}
          values={languages}
          onChange={setLanguages}
          placeholder="Add a language…"
        />
      </div>

      <div className="card">
        <h3 className="heading text-lg font-semibold mb-4">Tool Proficiencies</h3>
        <p className="text-sm text-dnd-help mb-4">
          Type to search Player's Handbook artisan's tools, instruments, gaming sets,
          or add your own.
        </p>
        <TagInputWithSuggestions
          label="Add a tool proficiency"
          suggestions={TOOL_PROFICIENCIES}
          values={toolProficiencies}
          onChange={setToolProficiencies}
          placeholder="Add a tool proficiency…"
        />
      </div>

      {/* WHY: Create mode shows the inline Create button. In edit mode the inline
          Update button is hidden entirely — the floating bar (shown only when dirty)
          is the sole save affordance, so it stays visible above the keyboard
          regardless of scroll position. */}
      {!isEdit && (
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="btn btn-gold w-full text-lg py-3"
        >
          {saving ? 'Saving...' : 'Create Character'}
        </button>
      )}

      {isEdit && dirty && (
        <div className="fixed bottom-0 left-0 right-0 z-20 p-3 bg-dnd-darker/95 border-none">
          <button
            type="submit"
            disabled={saving || !name.trim()}
            className="btn btn-gold w-full text-lg py-3"
          >
            {saving ? 'Saving...' : 'Update Character'}
          </button>
        </div>
      )}
    </form>
  );
}
